<template>
  <v-dialog
    v-model="dialog"
    max-width="1000px"
    persistent
    content-class="user-select-dialog sb-modal"
  >
    <template #activator="data">
      <v-tooltip top :disabled="disabled ? !enableTooltip : !buttonTooltip">
        <template v-slot:activator="{ on, attr }">
          <delete-confirm-modal
            v-if="assetReview"
            title=""
            header-title="Ownership Denied"
            button-class="error--text sb_btn_solid sb_btn_text"
            button-id="deleteMethod"
            button-text="Deny"
            button-icon="cancel"
            confirm-button-text="Yes"
            cancel-button-text="No"
            :custom-text="`You’re denying the ownership of this asset. Do you know who the correct owner is?`"
            @confirm="openDialog"
            @cancel="confirm([])"
          />
          <span v-else v-on="on">
            <v-btn
              :id="buttonId"
              :color="buttonColor"
              :small="smallButton || (!hasButtonText && hasButtonIcon)"
              :fab="!hasButtonText && hasButtonIcon"
              :disabled="disabled"
              :class="buttonClass"
              :outline="buttonOutline"
              v-bind="attr"
              @click="openDialog"
            >
              <v-icon v-if="hasButtonIcon" class="mr-1">
                {{ buttonIcon }}
              </v-icon>
              <span v-if="hasButtonText">
                {{ buttonText }}
              </span>
              <span v-if="!hasButtonText && !hasButtonIcon"> Select User </span>
            </v-btn>
          </span>
        </template>
        <span> {{ buttonTooltip }}</span>
      </v-tooltip>
    </template>
    <v-card>
      <v-card-title>
        <v-layout class="align-center">
          <v-flex>
            <h4>Select {{ singleUser ? 'a user' : 'users' }}</h4>
          </v-flex>
          <div class="sb-modal-close" @click="dialog = false">
            <v-icon>close</v-icon>
          </div>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <div v-if="bodyText">
          <p>{{ bodyText }}</p>
        </div>
        <v-layout>
          <v-flex xs12 lg6>
            <v-text-field
              v-model="userSearchValue"
              autofocus
              class="mt-0"
              label="User name, login, email, or domain"
              placeholder="John Doe"
              @keypress.enter="userSearch"
            />
          </v-flex>
        </v-layout>
        <div class="select-user-table mb-3">
          <sphere-table
            ref="userTable"
            :key="refreshTable"
            non-downloadable
            configurable-table-key="user-select-modal-results"
            :data-items="apiSearchValue ? undefined : []"
            :api-endpoint="
              assetReviewStep
                ? apiSearchValue
                  ? `/CommonApi/AllUserSearch?name=${apiSearchValue}`
                  : '/CommonApi/AllUserSearch'
                : apiSearchValue
                ? `/CommonApi/UserSearch?name=${apiSearchValue}`
                : '/CommonApi/UserSearch'
            "
            :columns="columns"
            :single-select="singleUser"
            table-type="actionable"
            name="Selecting a user"
            :page-length="5"
            :update-data-items="updateDataItems"
            @selectionchange="usersSelectionChange"
          >
            <template #noRecordsMsg>
              <div class="v-alert sb_NoDataAvialable info">
                <v-icon> info </v-icon>
                <div class="warning-message">
                  This search did not return any users!
                </div>
              </div>
            </template>
          </sphere-table>
        </div>
        <slot name="below-table" />
        <v-layout wrap row text-xs-right class="full_width">
          <v-flex lg12>
            <v-btn
              class="sb-btn-outline-neutral ma-0"
              depressed
              @click="dialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              class="sb-btn-primary my-0"
              depressed
              :disabled="
                disableConfirmBtn
                  ? true
                  : requireSelection
                  ? selectedItems.length === 0
                  : false
              "
              @click="confirm(selectedItems)"
            >
              {{ confirmBtnText(selectedItems) }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * This modal is used for selecting users. There are options for requiring a
 * selection, limiting selection to a single user, customizing the button, and
 * adding data to the response when a selection is made
 */
import DeleteConfirmModal from '../components/DeleteConfirmModal.vue';
import SphereTable from '../components/Table/SphereTable.vue';

export default {
  name: 'UserSelectDialog',
  components: { DeleteConfirmModal, SphereTable },
  props: {
    singleUser: {
      type: Boolean,
    },
    assetReview: {
      type: Boolean,
    },
    assetReviewStep: {
      type: Boolean,
      default: false,
    },
    requireSelection: {
      type: Boolean,
    },
    enrichResponseWith: {
      type: [Object, Array],
      default() {
        return undefined;
      },
    },
    existingIds: {
      type: [Array],
      default() {
        return [];
      },
    },
    buttonText: {
      type: String,
      default() {
        return undefined;
      },
    },
    buttonClass: {
      type: String,
      default() {
        return undefined;
      },
    },
    buttonOutline: {
      type: Boolean,
      default: false,
    },
    buttonId: {
      type: String,
      default() {
        return 'Add';
      },
    },
    buttonColor: {
      type: String,
      default() {
        return '';
      },
      validator(value) {
        return ['primary', 'success', 'info', 'error', 'warning'].includes(
          value
        );
      },
    },
    buttonIcon: {
      type: String,
      default() {
        return undefined;
      },
    },
    smallButton: {
      type: Boolean,
    },
    bodyText: {
      type: String,
      default() {
        return undefined;
      },
    },
    disabled: {
      type: Boolean,
    },
    buttonTooltip: {
      type: String,
      default() {
        return undefined;
      },
    },
    itemKey: {
      type: String,
      default() {
        return 'STSGuid';
      },
    },
    disableConfirmBtn: {
      type: Boolean,
    },
    disableSearch: {
      type: Boolean,
    },
    enableTooltip: {
      type: Boolean,
    },
  },
  data() {
    return {
      dialog: false,
      userSearchValue: '',
      apiSearchValue: '',
      selectedItems: [],
      refreshTable: false,
    };
  },
  computed: {
    hasButtonText() {
      return this.buttonText !== undefined;
    },
    hasButtonIcon() {
      return this.buttonIcon !== undefined;
    },
  },
  watch: {
    dialog(open) {
      if (!open) {
        this.close();
      }
    },
  },
  created() {
    this.columns = [
      {
        field: 'userName',
        defaultSort: 'asc',
      },
      {
        field: 'domain',
      },
      {
        field: 'SamAccountName',
      },
      {
        field: 'Email',
      },
    ];
  },

  methods: {
    confirmBtnText(selectedItems) {
      if (!this.requireSelection && selectedItems.length === 0) {
        return `Confirm`;
      }
      if (this.singleUser) {
        return `Confirm`;
      }
      return `Confirm (${selectedItems.length})`;
    },
    openDialog() {
      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.userTable.refreshTable();
      });
    },
    userSearch() {
      this.disableSearch = false;
      this.apiSearchValue = this.userSearchValue;
      this.$refs.userTable.filteredItemsCount = 0;
      this.$refs.userTable.totalItemsCount = 0;
      this.$refs.userTable.skip = 0;
      this.$nextTick(() => {
        this.$refs.userTable.refreshTable();
      });
    },
    close() {
      this.disableSearch = false;
      this.userSearchValue = '';
      this.apiSearchValue = '';
      this.selectedItems = [];
      this.$refs.userTable.filteredItemsCount = 0;
      this.$refs.userTable.totalItemsCount = 0;
      this.$refs.userTable.filteredItems = [];
      this.$refs.userTable.paginatedItems = [];
      this.$refs.userTable.hasNoItems = true;
      this.dialog = false;
      this.refreshTable = !this.refreshTable;
      this.$emit('close-user-dialog');
    },
    confirm(selectedItems) {
      const selectedIds = selectedItems.map((i) => i[this.itemKey]);
      this.$emit('selected-users', selectedIds, this.enrichResponseWith);
      this.$emit('selected-users-full', selectedItems);
      this.close();
    },
    usersSelectionChange(selItems) {
      if (this.existingIds.length)
        selItems = selItems.filter(
          (a) => !this.existingIds.includes(a.STSGuid)
        );

      this.selectedItems = selItems;
      this.$refs.userTable.selectedItemsCount = selItems.length;
    },
    updateDataItems(dataItems) {
      dataItems = dataItems.map((a) => {
        if (this.existingIds.includes(a.STSGuid)) {
          a.selectable = false;
        }
        return a;
      });
      return dataItems;
    },
  },
};
</script>
<style>
.showIconFirst {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.showIconFirst .v-icon {
  order: 1;
  margin-right: 4px;
  position: static;
}

.showIconFirst .sb_label {
  order: 0;
}

.showIconFirst span:not(.sb_label) {
  order: 2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
<style lang="scss">
.select-user-table {
  .sphere-table-container {
    position: relative !important;
  }
  .k-grid-container {
    max-height: calc(100vh - 480px);
    .sb_NoDataAvialable {
      justify-content: left;
      text-align: left;
      .v-icon {
        color: rgba(0, 0, 0, 0.3);
        font-size: 24px;
        margin-right: 15px;
      }
      > div {
        color: #fff;
      }
    }
  }
}
</style>
